import React from 'react';
import { useI18n } from '../i18n';

export default function UserManual({ onClose }) {
  const { locale } = useI18n();

  const content = MANUAL_CONTENT[locale] || MANUAL_CONTENT['es'];

  return (
    <div className="user-manual-overlay" onClick={onClose}>
      <div className="user-manual-modal" onClick={e => e.stopPropagation()}>
        <button className="user-manual-close" onClick={onClose}>✕</button>
        <h2>{content.title}</h2>
        {content.sections.map((section, idx) => (
          <div key={idx}>
            <h3>{section.heading}</h3>
            {section.paragraphs && section.paragraphs.map((p, pi) => (
              <p key={pi}>{p}</p>
            ))}
            {section.items && (
              <ul>
                {section.items.map((item, ii) => (
                  <li key={ii}>{item}</li>
                ))}
              </ul>
            )}
            {section.pcOnly && (
              <div className="pc-only">
                🖥️ {section.pcOnly}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const MANUAL_CONTENT = {
  es: {
    title: '📖 Manual de Usuario — EV Route Planner',
    sections: [
      {
        heading: '🗺️ Pestaña Mapa',
        paragraphs: [
          'El mapa muestra tu ruta y los cargadores de vehículos eléctricos cercanos. Puedes interactuar con él de varias formas:'
        ],
        items: [
          'Modo Ruta: Calcula rutas entre origen y destino. Haz doble clic en el mapa para explorar cargadores cercanos a cualquier punto.',
          'Modo Cargadores: Explora todos los cargadores de la zona visible del mapa. Se cargan automáticamente al hacer zoom o mover el mapa.',
          'Usa "Marcar origen" y "Marcar destino" para seleccionar puntos directamente en el mapa haciendo clic.',
          'Usa "Marcar punto de Paso" para añadir paradas intermedias en tu ruta.',
          'Radio de búsqueda (km): En modo Ruta, indica la distancia máxima de desviación para buscar cargadores. Puedes editar el valor, borrarlo completamente (se restablecerá a 5) y pulsar Intro o el botón 🔄 para actualizar.',
          'Mostrar: Precios / kW: Activa estos indicadores para ver bocadillos informativos sobre cada cargador en el mapa.',
          'Si la carga de datos tarda más de 1 segundo, aparecerá un indicador "Cargando..." en el centro del mapa.',
        ],
        pcOnly: 'En PC, al pasar el ratón sobre la ruta se muestra la distancia en km desde el origen. Haz clic para fijar puntos de interés.'
      },
      {
        heading: '⚡ Cargadores',
        paragraphs: [
          'Haz clic en cualquier marcador de cargador en el mapa para ver información detallada:'
        ],
        items: [
          'Operador, potencia máxima, precio estimado y tipos de conectores.',
          'Distancia de desviación respecto a tu ruta.',
          'Puedes añadir el cargador como parada en tu ruta o marcarlo como favorito (★).',
          'Usa los botones — (minimizar) y □ (expandir) para gestionar la ventana de información.',
          'Los operadores principales se identifican con letras: [T] Tesla, [Z] Zunder, [I] Iberdrola, [R] Repsol, etc.',
        ],
      },
      {
        heading: '🔍 Leyendas y Filtros',
        items: [
          'Potencias: Filtra cargadores por rangos de potencia (kW). Los rangos son personalizables haciendo clic en el texto "Clic para configurar".',
          'Tipos de Conector: Filtra por tipo de conector (CCS, CHAdeMO, Type 2, etc.).',
          'Operadores: Filtra por compañía operadora. Los favoritos aparecen primero.',
          'Todas las leyendas son arrastrables (también en móvil) y minimizables.',
          'El indicador de "Doble clic..." se puede mover y cerrar.',
        ],
      },
      {
        heading: '📍 Paradas y Puntos km',
        paragraphs: [
          'En la pestaña "Paradas" puedes configurar puntos kilométricos donde buscar cargadores, el radio de desviación y la autonomía de tu vehículo.'
        ],
        items: [
          'Sugerencia automática: Calcula paradas óptimas según la autonomía de tu vehículo.',
          'Puntos km manuales: Añade puntos específicos donde quieras buscar cargadores.',
        ],
        pcOnly: 'En PC puedes fijar puntos km haciendo clic directamente sobre la línea de la ruta en el mapa.'
      },
      {
        heading: '⭐ Favoritos',
        paragraphs: [
          'Los cargadores marcados como favoritos se guardan en tu perfil (si has iniciado sesión) o en el almacenamiento local del navegador.'
        ],
      },
      {
        heading: '🔎 Buscar Cargadores',
        paragraphs: [
          'En la pestaña "Cargadores" puedes buscar cargadores por municipio y aplicar filtros avanzados de corriente, conectores y potencia.'
        ],
      },
      {
        heading: '🌐 Idioma y Tema',
        items: [
          'Cambia el idioma desde el menú desplegable en la esquina superior derecha. Idiomas disponibles: Español, English, Français, Português.',
          'Alterna entre tema claro y oscuro con el botón 🌙/☀️.',
          'Tus preferencias se guardan automáticamente.',
        ],
      },
      {
        heading: '💡 Sugerencias',
        paragraphs: [
          'En la pestaña "Sugerencias" puedes enviar ideas y mejoras para la aplicación.'
        ],
      },
    ],
  },
  en: {
    title: '📖 User Manual — EV Route Planner',
    sections: [
      {
        heading: '🗺️ Map Tab',
        paragraphs: ['The map shows your route and nearby electric vehicle chargers. You can interact with it in several ways:'],
        items: [
          'Route Mode: Calculate routes between origin and destination. Double-click on the map to explore nearby chargers at any point.',
          'Chargers Mode: Explore all chargers in the visible map area. They load automatically when you zoom or pan.',
          'Use "Mark origin" and "Mark destination" to select points directly on the map by clicking.',
          'Use "Mark waypoint" to add intermediate stops to your route.',
          'Search radius (km): In Route mode, set the maximum deviation distance. You can clear and re-enter the value, then press Enter or the 🔄 button to update.',
          'Show: Prices / kW: Toggle these indicators to see info bubbles on each charger on the map.',
          'If data loading takes more than 1 second, a "Loading..." indicator will appear in the center of the map.',
        ],
        pcOnly: 'On PC, hovering over the route shows the distance in km from the origin. Click to pin points of interest.'
      },
      {
        heading: '⚡ Chargers',
        paragraphs: ['Click on any charger marker on the map to see detailed information:'],
        items: [
          'Operator, max power, estimated price, and connector types.',
          'Deviation distance from your route.',
          'You can add the charger as a stop on your route or mark it as a favorite (★).',
          'Use the — (minimize) and □ (expand) buttons to manage the info window.',
          'Main operators are identified by letters: [T] Tesla, [Z] Zunder, [I] Iberdrola, [R] Repsol, etc.',
        ],
      },
      {
        heading: '🔍 Legends and Filters',
        items: [
          'Power: Filter chargers by power ranges (kW). Ranges are customizable by clicking the "Click to configure" text.',
          'Connector Types: Filter by connector type (CCS, CHAdeMO, Type 2, etc.).',
          'Operators: Filter by operating company. Favorites appear first.',
          'All legends are draggable (also on mobile) and minimizable.',
          'The "Double-click..." indicator can be moved and closed.',
        ],
      },
      {
        heading: '📍 Stops and km Points',
        paragraphs: ['In the "Stops" tab you can configure km points to search for chargers, deviation radius, and your vehicle range.'],
        items: [
          'Auto suggestion: Calculates optimal stops based on your vehicle range.',
          'Manual km points: Add specific points where you want to search for chargers.',
        ],
        pcOnly: 'On PC you can pin km points by clicking directly on the route line on the map.'
      },
      { heading: '⭐ Favorites', paragraphs: ['Chargers marked as favorites are saved to your profile (if logged in) or in local browser storage.'] },
      { heading: '🔎 Search Chargers', paragraphs: ['In the "Chargers" tab you can search for chargers by municipality and apply advanced filters for current type, connectors, and power.'] },
      {
        heading: '🌐 Language and Theme',
        items: [
          'Change the language from the dropdown menu in the upper right corner. Available: Español, English, Français, Português.',
          'Toggle between light and dark theme with the 🌙/☀️ button.',
          'Your preferences are saved automatically.',
        ],
      },
      { heading: '💡 Suggestions', paragraphs: ['In the "Suggestions" tab you can submit ideas and improvements for the application.'] },
    ],
  },
  fr: {
    title: '📖 Manuel Utilisateur — EV Route Planner',
    sections: [
      {
        heading: '🗺️ Onglet Carte',
        paragraphs: ['La carte affiche votre itinéraire et les bornes de recharge à proximité. Vous pouvez interagir de plusieurs façons :'],
        items: [
          "Mode Itinéraire : Calculez des itinéraires entre l'origine et la destination. Double-cliquez sur la carte pour explorer les bornes proches.",
          'Mode Bornes : Explorez toutes les bornes dans la zone visible. Elles se chargent automatiquement lors du zoom ou du déplacement.',
          'Utilisez « Marquer origine » et « Marquer destination » pour sélectionner des points directement sur la carte.',
          'Utilisez « Marquer point de passage » pour ajouter des arrêts intermédiaires.',
          'Rayon de recherche (km) : En mode Itinéraire, définissez la déviation maximale. Vous pouvez effacer et resaisir la valeur, puis appuyer sur Entrée ou le bouton 🔄.',
          'Afficher : Prix / kW : Activez ces indicateurs pour voir des bulles informatives sur chaque borne.',
          'Si le chargement prend plus d\'une seconde, un indicateur « Chargement... » apparaîtra au centre de la carte.',
        ],
        pcOnly: "Sur PC, survolez l'itinéraire pour afficher la distance en km depuis l'origine. Cliquez pour épingler des points d'intérêt."
      },
      {
        heading: '⚡ Bornes de recharge',
        paragraphs: ['Cliquez sur un marqueur de borne pour voir les informations détaillées :'],
        items: [
          'Opérateur, puissance max, prix estimé et types de connecteurs.',
          "Distance de déviation par rapport à votre itinéraire.",
          'Vous pouvez ajouter la borne comme arrêt ou la marquer en favori (★).',
          'Utilisez les boutons — (réduire) et □ (agrandir) pour gérer la fenêtre info.',
          'Les opérateurs principaux sont identifiés par des lettres : [T] Tesla, [Z] Zunder, [I] Iberdrola, [R] Repsol, etc.',
        ],
      },
      {
        heading: '🔍 Légendes et Filtres',
        items: [
          'Puissances : Filtrez par plages de puissance (kW). Les plages sont personnalisables en cliquant sur le texte « Cliquer pour configurer ».',
          'Types de connecteur : Filtrez par type de connecteur (CCS, CHAdeMO, Type 2, etc.).',
          "Opérateurs : Filtrez par société. Les favoris apparaissent en premier.",
          'Toutes les légendes sont déplaçables (aussi sur mobile) et réductibles.',
          'L\'indicateur « Double-clic... » peut être déplacé et fermé.',
        ],
      },
      {
        heading: '📍 Arrêts et Points km',
        paragraphs: ["Dans l'onglet « Arrêts », configurez les points km, le rayon de déviation et l'autonomie de votre véhicule."],
        items: [
          'Suggestion automatique : Calcule les arrêts optimaux selon votre autonomie.',
          'Points km manuels : Ajoutez des points spécifiques pour rechercher des bornes.',
        ],
        pcOnly: "Sur PC, vous pouvez épingler des points km en cliquant directement sur la ligne de l'itinéraire."
      },
      { heading: '⭐ Favoris', paragraphs: ['Les bornes marquées en favori sont enregistrées dans votre profil (si connecté) ou dans le stockage local du navigateur.'] },
      { heading: '🔎 Rechercher des bornes', paragraphs: ["Dans l'onglet « Bornes », recherchez par commune et appliquez des filtres avancés."] },
      {
        heading: '🌐 Langue et Thème',
        items: [
          'Changez la langue depuis le menu déroulant en haut à droite. Disponibles : Español, English, Français, Português.',
          'Basculez entre thème clair et sombre avec le bouton 🌙/☀️.',
          'Vos préférences sont enregistrées automatiquement.',
        ],
      },
      { heading: '💡 Suggestions', paragraphs: ["Dans l'onglet « Suggestions », soumettez vos idées et améliorations."] },
    ],
  },
  pt: {
    title: '📖 Manual do Utilizador — EV Route Planner',
    sections: [
      {
        heading: '🗺️ Separador Mapa',
        paragraphs: ['O mapa mostra a sua rota e os carregadores de veículos elétricos próximos. Pode interagir de várias formas:'],
        items: [
          'Modo Rota: Calcule rotas entre origem e destino. Faça duplo clique no mapa para explorar carregadores próximos.',
          'Modo Carregadores: Explore todos os carregadores na zona visível do mapa. Carregam automaticamente ao fazer zoom ou mover.',
          'Use "Marcar origem" e "Marcar destino" para selecionar pontos diretamente no mapa.',
          'Use "Marcar ponto de passagem" para adicionar paragens intermédias.',
          'Raio de pesquisa (km): No modo Rota, indica o desvio máximo. Pode limpar e voltar a escrever o valor, premindo Enter ou o botão 🔄.',
          'Mostrar: Preços / kW: Ative estes indicadores para ver balões informativos sobre cada carregador.',
          'Se o carregamento demorar mais de 1 segundo, aparecerá um indicador "A carregar..." no centro do mapa.',
        ],
        pcOnly: 'No PC, ao passar o rato sobre a rota é mostrada a distância em km desde a origem. Clique para fixar pontos de interesse.'
      },
      {
        heading: '⚡ Carregadores',
        paragraphs: ['Clique em qualquer marcador de carregador no mapa para ver informação detalhada:'],
        items: [
          'Operador, potência máxima, preço estimado e tipos de conectores.',
          'Distância de desvio relativamente à sua rota.',
          'Pode adicionar o carregador como paragem na rota ou marcá-lo como favorito (★).',
          'Use os botões — (minimizar) e □ (expandir) para gerir a janela de informação.',
          'Os operadores principais são identificados com letras: [T] Tesla, [Z] Zunder, [I] Iberdrola, [R] Repsol, etc.',
        ],
      },
      {
        heading: '🔍 Legendas e Filtros',
        items: [
          'Potências: Filtre carregadores por intervalos de potência (kW). Os intervalos são personalizáveis clicando no texto "Clique para configurar".',
          'Tipos de Conector: Filtre por tipo de conector (CCS, CHAdeMO, Type 2, etc.).',
          'Operadores: Filtre por empresa operadora. Os favoritos aparecem primeiro.',
          'Todas as legendas são arrastáveis (também no telemóvel) e minimizáveis.',
          'O indicador "Duplo clique..." pode ser movido e fechado.',
        ],
      },
      {
        heading: '📍 Paragens e Pontos km',
        paragraphs: ['No separador "Paragens" pode configurar pontos quilométricos onde pesquisar carregadores, o raio de desvio e a autonomia do veículo.'],
        items: [
          'Sugestão automática: Calcula paragens ótimas segundo a autonomia do veículo.',
          'Pontos km manuais: Adicione pontos específicos onde queira pesquisar carregadores.',
        ],
        pcOnly: 'No PC pode fixar pontos km clicando diretamente sobre a linha da rota no mapa.'
      },
      { heading: '⭐ Favoritos', paragraphs: ['Os carregadores marcados como favoritos são guardados no seu perfil (se tiver sessão iniciada) ou no armazenamento local do navegador.'] },
      { heading: '🔎 Pesquisar Carregadores', paragraphs: ['No separador "Carregadores" pode pesquisar por localidade e aplicar filtros avançados de corrente, conectores e potência.'] },
      {
        heading: '🌐 Idioma e Tema',
        items: [
          'Mude o idioma no menu suspenso no canto superior direito. Disponíveis: Español, English, Français, Português.',
          'Alterne entre tema claro e escuro com o botão 🌙/☀️.',
          'As suas preferências são guardadas automaticamente.',
        ],
      },
      { heading: '💡 Sugestões', paragraphs: ['No separador "Sugestões" pode enviar ideias e melhorias para a aplicação.'] },
    ],
  },
};
